/** Central rendering constants (geometry, colors, typography) — Miragon corporate identity. */

import { DEFAULT_PLOT_SIZE } from "@miragon/wardley-schema-model";
import { MIRAGON } from "../theme/index.js";

/**
 * Default plot area in diagram px (interior without axis margins). Changeable at runtime via
 *  config.size. Width/height come from schema-model (single source, also used for migrations).
 */
export const PLOT = {
  width: DEFAULT_PLOT_SIZE.width,
  height: DEFAULT_PLOT_SIZE.height,
  marginLeft: 66,
  marginTop: 38,
  marginRight: 44,
  marginBottom: 58,
} as const;

/** Smallest drawn plot — the size policy lives in schema-model (shared with the DSL parser). */
export { PLOT_MIN } from "@miragon/wardley-schema-model";

export const NODE_SIZE = 34;
export const COMPONENT_RADIUS = 15;
export const COMPONENT_INNER_RADIUS = 12;
/**
 * The mark on an element whose link the editor opens (its context pad's "Open link"): editor
 * chrome in the `--cd-ui-*` roles of `wardley.css`, which the export leaves out.
 */
export const LINK_BADGE_CLASS = "wardley-link-badge";
export const LINK_BADGE_RADIUS = 8;
export const LINK_BADGE_ICON_SIZE = 11;
export const ANCHOR_ICON_SIZE = 26;
export const PIPELINE_HEIGHT = 30;
export const PIPELINE_ANCHOR_SIZE = 16;

export const NOTE_LINE_HEIGHT = 17;
const NOTE_PAD_X = 8;
const NOTE_PAD_Y = 6;
const NOTE_CHAR_W = 7.5;

/**
 * Box dimensions of a note from its (possibly multi-line) text. The note shape grows as large as
 * its text -> the move/click hitbox grows with it (instead of a fixed 34px). Minimum = NODE_SIZE.
 */
export function noteMetrics(label: string): { lines: string[]; width: number; height: number } {
  const lines = (label && label.length ? label : "note").split("\n");
  const maxLen = Math.max(1, ...lines.map((l) => l.length));
  return {
    lines,
    width: Math.max(NODE_SIZE, Math.round(maxLen * NOTE_CHAR_W) + NOTE_PAD_X * 2),
    height: Math.max(NODE_SIZE, lines.length * NOTE_LINE_HEIGHT + NOTE_PAD_Y * 2),
  };
}

/**
 * The status colours the notation drew with before design system 1.13.0 brightened its status
 * layer. Maps store note colours as hex, the accelerator needs 4.5:1 as text and the SVG export
 * must not change, so the notation keeps them as its own literals.
 */
// Notation colours until the joint dark mode maps them onto the status roles (guide §3.6).
// brand-lint-disable
const NOTATION_STATUS = {
  success: "#0B7A55",
  warning: "#92610A",
  danger: "#C92A2A",
  info: "#2B50D4",
} as const;
// brand-lint-enable

/**
 * Canvas colours — Miragon palette (blue = interactive/flow, green = accelerate/positive,
 * red = movement/decelerate). Keys are the public contract; brand values come from the single source
 * of truth in `theme/palette.ts` (`MIRAGON`). Referenced (not spread) so the literal-string types
 * survive into the published `.d.ts`.
 */
export const COLORS = {
  paper: MIRAGON.grau,
  ink: MIRAGON.schwarz,
  inkSoft: MIRAGON.textLeise,
  axis: MIRAGON.kontur,
  axisText: MIRAGON.textLeise,
  grid: MIRAGON.linie,
  band: "rgba(29,29,29,0.025)",
  stroke: MIRAGON.schwarz,
  componentFill: MIRAGON.weiss,
  anchorFill: MIRAGON.schwarz,
  dependency: MIRAGON.kontur,
  accent: MIRAGON.blau,
  accentSoft: "rgba(51,93,229,0.12)",
  flow: MIRAGON.blau,
  movement: NOTATION_STATUS.danger,
  inertia: MIRAGON.schwarz,
  noteText: MIRAGON.textLeise,
  pipeline: MIRAGON.schwarz,
  // An element surface like the components (guide §10.1); its stroke and number set it apart.
  annotationFill: MIRAGON.weiss,
  plotBackground: MIRAGON.weiss,
  accelerator: NOTATION_STATUS.success,
  deaccelerator: NOTATION_STATUS.danger,
} as const;

/**
 * Note color palette (bpmn.io-style mini picker; deliberately NOT a full color picker). Values are
 * hex and are rendered 1:1 as CSS color, or serialized in the DSL as `(color …)`.
 * Convention (also used in the Wardley-mapping skill): green = good, red = problem/risk,
 * amber = watch, blue = info, purple = idea, slate = neutral.
 */
export const NOTE_COLORS = [
  { id: "green", name: "Green · good", value: NOTATION_STATUS.success },
  { id: "amber", name: "Amber · watch", value: NOTATION_STATUS.warning },
  { id: "red", name: "Red · problem", value: NOTATION_STATUS.danger },
  { id: "blue", name: "Blue · info", value: NOTATION_STATUS.info },
  // Maps store these as hex; guide §3.6 retires them with the joint dark mode.
  // brand-lint-disable
  { id: "teal", name: "Teal", value: "#0E8181" },
  { id: "purple", name: "Purple · idea", value: "#6A3DB8" },
  { id: "pink", name: "Pink", value: "#C2185B" },
  { id: "slate", name: "Slate · neutral", value: "#4A4A4A" },
  // brand-lint-enable
] as const;

/**
 * Colors of the attitude regions per kind (shared by renderer + palette icons). Miragon palette:
 * pioneers = blue (explore), settlers = amber (stabilise), town planners = green (industrialise).
 */
export const ATTITUDE_COLORS: Record<string, { fill: string; stroke: string }> = {
  pioneers: { fill: "rgba(51,93,229,0.07)", stroke: MIRAGON.blau },
  // The notation's warning as a tint, until the joint dark mode draws Settlers per guide §3.6.
  // brand-lint-disable-next-line
  settlers: { fill: "rgba(146,97,10,0.10)", stroke: NOTATION_STATUS.warning },
  // Bright brand green only as the fill; the title and outline need the darker success value (AA).
  townplanners: { fill: "rgba(0,230,118,0.10)", stroke: NOTATION_STATUS.success },
};

/** Canonical region titles (Wardley's Pioneers / Settlers / Town Planners), keyed by kind. */
export const ATTITUDE_TITLES: Record<string, string> = {
  pioneers: "Pioneers",
  settlers: "Settlers",
  townplanners: "Town Planners",
};

export const FONT = {
  /**
   * Set as the `font-family` attribute DIRECTLY on all SVG text elements (canvas labels,
   * axis/stage text and consequently the SVG export too) – not inherited from a CSS container.
   * 'Geist Variable' comes first because that is the family name `@fontsource-variable/geist`
   * registers; 'Geist' matches a locally installed (static) Geist. Without either it falls back to a
   * system sans. The library does NOT ship the font – consumers self-host it, see README.
   */
  family:
    "'Geist Variable', 'Geist', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
  label: 13,
  axis: 12,
  stage: 12,
} as const;
